import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack';
import { FetchError } from 'ofetch';
import type { FetchContext, FetchResponse } from 'ofetch';

interface APIErrorDetail {
  type: string;
  message: string;
}

class APIError extends Error {
  code: number;
  details: APIErrorDetail[];

  constructor(code: number, message: string, details: APIErrorDetail[]) {
    super(message);
    this.code = code;
    this.details = details;

    Object.setPrototypeOf(this, APIError.prototype);
  }
}

type ClientErrorType =
  | 'OFFLINE'
  | 'TIMEOUT'
  | 'NETWORK'
  | 'ABORTED';

class ClientSideNetworkError extends Error {
  type: ClientErrorType;

  constructor(type: ClientErrorType, message: string) {
    super(message);
    this.name = 'ClientSideNetworkError';
    this.type = type;
  }
}

const camelToSnake = (key: string): string => {
  return key.replace(/[A-Z]/g, function(match) {
    return '_' + match.toLowerCase();
  });
};

const handleQuery = <T extends NitroFetchRequest>(
  query?: NitroFetchOptions<T>['query']
) => {
  if (!query) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const snakeQuery: Record<string, any> = {};

  for (const key in query) {
    const value = query[key];

    if (!value) {
      continue;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => (snakeQuery[camelToSnake(key)] = item));
    } else {
      snakeQuery[camelToSnake(key)] = value;
    }
  }

  return snakeQuery;
};

function getAccessToken() {
  const authStore = useAuthStore();
  return authStore.isAuthenticated
    ? authStore.accessToken
    : authStore.isGuest
      ? authStore.guest?.token.accessToken
      : '';
}

function addOrUpdateHeader(headers, name, value, override = true) {
  if (headers.has(name)) {
    if (override) {
      headers.set(name, value);
    }
  } else {
    headers.append(name, value);
  }
}

function addCommonHeaders(headers: Headers) {
  addOrUpdateHeader(
    headers,
    'Access-Control-Allow-Methods',
    'POST, GET, OPTIONS, PUT, DELETE'
  );
  addOrUpdateHeader(headers, 'Access-Control-Allow-Origin', '*');
  addOrUpdateHeader(headers, 'Content-Type', 'application/json', false);
  addOrUpdateHeader(headers, 'X-Client-App', __APP_NAME__);
  addOrUpdateHeader(headers, 'X-Client-Version', __APP_VERSION__);
}

function handleHeaders(headers: Headers) {
  const accessToken = getAccessToken();
  addCommonHeaders(headers);

  if (accessToken) {
    addOrUpdateHeader(headers, 'Authorization', `bearer ${accessToken}`);
  }

  return headers;
}

const onRequestError = () => {
  if (!navigator.onLine) {
    const message = 'Request timed out. Please check your connection and try again.';
    throw new ClientSideNetworkError('OFFLINE', message);
  }

  throw new APIError(504, 'Timeout', []);
};

const isTimeoutError = (error: unknown): boolean => {
  if (!(error instanceof FetchError)) {
    return false;
  }

  if (error.name === 'AbortError') {
    return true;
  }

  if (
    error.cause instanceof DOMException &&
    error.cause.name === 'AbortError'
  ) {
    return true;
  }

  return false;
};

const isCloudflareError = (status: number): boolean => {
  return status >= 520 && status <= 529;
};

const isServerError = (status: number): boolean => {
  return status >= 500 && status < 600;
};

const getServerErrorMessage = (status: number): string => {
  if (isCloudflareError(status)) {
    return 'Service temporarily unavailable. Please try again in a moment.';
  }

  switch (status) {
    case 500:
      return 'An unexpected server error occurred. Please try again later.';
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again in a moment.';
    default:
      return 'A server error occurred. Please try again later.';
  }
};

const isJsonResponse = (data: unknown): data is Record<string, unknown> => {
  return typeof data === 'object' && data !== null && !Array.isArray(data);
};

const onResponseError = (
  context: FetchContext & { response: FetchResponse<ResponseType> }
) => {
  const error = context.error;
  const status = context.response.status;

  if (error instanceof FetchError) {
    if (isTimeoutError(context.error)) {
      throw new ClientSideNetworkError('TIMEOUT', 'Request timed out');
    }

    if (!error.response) {
      throw new ClientSideNetworkError('NETWORK', 'Network error');
    }
  }

  if (isCloudflareError(status)) {
    throw new ClientSideNetworkError('NETWORK', getServerErrorMessage(status));
  }

  let details: APIErrorDetail[] = [];
  const responseData = context.response._data;

  if (isJsonResponse(responseData) && responseData.detail !== undefined) {
    if (Array.isArray(responseData.detail)) {
      details = responseData.detail.map(({ type, msg, loc: _loc }) => {
        return {
          type: type,
          message: `${msg.charAt(0).toUpperCase() + msg.slice(1)}`
        };
      });
    } else {
      details = [
        {
          type: context.response.type,
          message: String(responseData.detail)
        }
      ];
    }
  }

  const detailMessage = details
    .map(({ message }) => message)
    .filter(Boolean)
    .join('\n');

  const message =
    detailMessage ||
    (isServerError(status) ? getServerErrorMessage(status) : context.response.statusText);

  switch (context.response.status) {
    case 403:
    case 404: {
      break;
    }

    case 412: {
      // Stripe confirmation
      return;
    }

    case 426: {
      eventBus.emit(
        Events.SERVICE_ERROR,
        'A new version is available. Please refresh your browser to load the latest version. If the issue persists, try closing and reopening the app or clearing your browser cache.'
      );

      return;
    }

    default: {
      eventBus.emit(Events.SERVICE_ERROR, message);
    }
  }

  throw new APIError(context.response.status, message, details);
};

export {
  handleHeaders,
  handleQuery,
  onRequestError,
  onResponseError,
  APIError,
  ClientSideNetworkError
};
